<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:settings-2',
	name: '$t:blocks.input_slider.name',
	description: '$t:blocks.input_slider.description',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: { supportedTypes: ({ primitiveType }) => {
		if (isPrimitiveTypeDecimal(primitiveType)) return [];
		return ['number'];
	}, recommendedDisplays: ['$builtin:DisplayFormattedValue']	},

	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'min',
			label: '$t:blocks.common.props.min',
		},
		{
			name: 'max',
			label: '$t:blocks.common.props.max',
		},
		{
			name: 'step',
			label: '$t:blocks.common.props.step',
		},
		{
			name: 'color',
			label: '$t:blocks.common.props.color',
			block: BuiltinBlockIds.InputSemanticColor,
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'block',
			label: '$t:blocks.common.props.block',
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
	],
}
</script>
<script lang="ts" setup>
import type { SliderProps } from '@nuxt/ui';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { NumberFieldOptions } from '#layers/data-model/public';
import { reactivePick } from '@vueuse/core';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed } from 'vue';
import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS } from '#layers/blocks/constants/ui-options';
import { isPrimitiveTypeDecimal } from '#layers/data-model/public';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice, useReadonly } from '#layers/ui/public';

interface Props extends
	Pick<NumberFieldOptions, 'min' | 'max' | 'step'>,
	Pick<InputBlockProps, 'disabled' | 'disabledNotice' | 'readonly'>,
	Pick<SliderProps, 'color' | 'size'> {
	/**
	 * Render the input full width
	 */
	block?: boolean;
}

const props = defineProps<Props>();
const model = defineModel<number>();

;

const forwarded = computed(() => ({
	...reactivePick(props, 'color', 'size'),
	min: props.min !== undefined ? Number(props.min) : undefined,
	max: props.max !== undefined ? Number(props.max) : undefined,
	step: props.step !== undefined ? Number(props.step) : undefined,
}));
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly);
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DSlider
			v-model="modelValue"
			v-bind="forwarded"
			tooltip
			:disabled
			:aria-readonly="readonly"
			:class="{
				'w-full flex-1': block,
				'pointer-events-none': readonly,
			}"
			@pointerdown.capture="preventPointer"
			@click.capture="preventPointer"
			@keydown.capture="preventKeyboardNavigation"
		/>
	</DTooltip>
</template>
